import ReactMarkdown from 'react-markdown'
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  memo,
  useState,
  Children,
  isValidElement,
  type ReactElement,
  type ReactNode,
  type InputHTMLAttributes,
  type TableHTMLAttributes,
} from 'react'
import remarkGfm from 'remark-gfm'
import rehypeHighlight from 'rehype-highlight'
import rehypeRaw from 'rehype-raw'
import rehypeSanitize from 'rehype-sanitize'
import 'highlight.js/styles/github.css' // You can change this to other highlight.js themes
import { ImageLightboxDialog } from '@/components/features/image-lightbox-dialog'
import { slugifyHeading } from '@/lib/heading-utils'
import {
  childrenToText,
  getMermaidSourceFromPreChildren,
  getHtmlSourceFromPreChildren,
  getStatusBoardSourceFromPreChildren,
  getReviewChangelogSourceFromPreChildren,
  getAudioSourceFromPreChildren,
  getBasecampDraftSourceFromPreChildren,
} from '@/lib/markdown-code-block'
import { AudioFencePlayerLive } from '@/components/features/audio-fence-player-live'
import { leadingAudioFence, parseAudioFenceYaml, stripLeadingAudioFence } from '@/lib/audio-fence'
import { MermaidDiagram } from '@/components/features/mermaid-diagram'
import { HtmlBlockPreview } from '@/components/features/html-block-preview'
import { StatusBoardFenceView } from '@/components/features/status-board-fence-view'
import { BasecampDraftFenceView } from '@/components/features/basecamp-draft-fence-view'
import {
  PreviewBookmarkedHeadingContext,
  PreviewReadingGutter,
} from '@/components/features/preview-reading-gutter'
import { useAgentPanelVisible } from '@/hooks/use-agent-panel-visible'
import { useSoftBookmark } from '@/hooks/use-soft-bookmark'
import { useSoftBookmarksEnabled } from '@/hooks/use-soft-bookmarks-enabled'
import { AuthenticatedImage } from '@/components/features/authenticated-image'
import { ensureSessionImageMarkdown } from '@/lib/session-image-card'
import { parseMarkdownWithFrontmatter } from '@/lib/markdown-frontmatter'
import { testIds } from '@/lib/test-ids'
import { cn } from '@/lib/utils'
import { chatMarkdownUrlTransform } from '@/lib/chat-markdown-links'
import { absoluteLocalDevHref, hrefForInAppDocumentNavigation, hrefForInAppNavigation, isMaaterNonDocumentAppHref } from '@/lib/maater-document-url'
import { isInPageFragmentHref, scrollToInPageAnchor } from '@/lib/in-page-anchor'
import { InAppRouteLink } from '@/components/features/shared/in-app-route-link'
import './document-reading-prose.css'

/**
 * Chat writes `[title](doc:uuid)`. rehype-sanitize drops unknown protocols, so
 * the preview anchor arrives with an empty href and the click does nothing.
 * Rewrite to `/doc/uuid` before sanitizing.
 */
function remarkRewriteDocProtocolLinks() {
  return (tree: { children?: unknown[] }) => {
    const visit = (node: any) => {
      if (node?.type === 'link' && typeof node.url === 'string') {
        const match = node.url.match(/^doc:([0-9a-f-]{36})$/i)
        if (match) node.url = `/doc/${match[1]}`
      }
      if (Array.isArray(node?.children)) node.children.forEach(visit)
    }
    visit(tree)
  }
}

/** Module-level singletons so ReactMarkdown plugin identity is stable across renders. */
const MARKDOWN_REMARK_PLUGINS = [remarkRewriteDocProtocolLinks, remarkGfm]
/**
 * TipTap + turndown-plugin-gfm keep headerless tables as raw HTML (`keep` when
 * the first row is not `<th>`/`<thead>`). Edit mode (marked + TipTap) renders
 * that HTML; preview must parse it too or the tags show as text. Sanitize after
 * raw so scripts/handlers never run. Highlight last so hljs classes survive.
 */
const MARKDOWN_REHYPE_PLUGINS = [rehypeRaw, rehypeSanitize, rehypeHighlight]

type MarkdownImageProps = {
  src?: string
  alt?: string
  /** From raw `<img width>` written when an image is resized in the editor */
  width?: string | number
  /** Opens full-screen preview with pan/zoom when user activates the thumbnail */
  onOpenPreview: (rawSrc: string, altCaption: string) => void
}

/** Thumbnail keeps stable resolution; clicks open modal (below) instead of reloading the tree.
 *  Attachment URLs go through AuthenticatedImage (Bearer + blob) because <img> cannot send JWT. */
const MarkdownImage = memo(function MarkdownImage({ src, alt, width, onOpenPreview }: MarkdownImageProps) {
  if (!src) return null
  const title = alt?.trim() ? `${alt} — click to enlarge` : 'Click to enlarge'
  return (
    <button
      type="button"
      data-testid={testIds.markdownPreview.imageThumbnail}
      title={title}
      aria-label={alt?.trim() ? `Open full-size preview: ${alt}` : 'Open image preview'}
      className={cn(
        'not-prose inline-block max-w-full cursor-zoom-in rounded-md border-0 bg-transparent p-0',
        'ring-offset-background outline-none transition-[filter]',
        'hover:brightness-[1.03] focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
      )}
      onClick={(e) => {
        e.preventDefault()
        e.stopPropagation()
        onOpenPreview(src, alt || '')
      }}
    >
      <AuthenticatedImage
        rawSrc={src}
        alt={alt || ''}
        width={width}
        className="pointer-events-none h-auto max-w-full select-none rounded-[inherit]"
        loading="lazy"
        draggable={false}
      />
    </button>
  )
})

/** Strip YAML frontmatter (--- ... ---) from start of content so it isn't rendered as markdown. */
function stripFrontmatter(content: string): string {
  if (!content.startsWith('---')) return content
  const end = content.indexOf('\n---', 3)
  if (end === -1) return content
  return content.slice(end + 4).trimStart()
}

function isCheckboxInput(child: unknown): child is ReactElement<InputHTMLAttributes<HTMLInputElement>> {
  return (
    isValidElement(child) &&
    child.type === 'input' &&
    (child.props as InputHTMLAttributes<HTMLInputElement>).type === 'checkbox'
  )
}

/** GFM task items hoist checkbox + inline nodes as direct li children; wrap text in one flex child like TipTap. */
function splitTaskItemChildren(children: ReactNode): {
  checkbox: ReactNode | null
  content: ReactNode[]
} {
  const childArray = Children.toArray(children)

  const soleParagraph =
    childArray.length === 1 && isValidElement(childArray[0]) && childArray[0].type === 'p'

  if (soleParagraph) {
    const paragraph = childArray[0] as ReactElement<{ children?: ReactNode }>
    const paragraphChildren = Children.toArray(paragraph.props.children)
    const checkboxIndex = paragraphChildren.findIndex(isCheckboxInput)
    if (checkboxIndex >= 0) {
      return {
        checkbox: paragraphChildren[checkboxIndex],
        content: paragraphChildren.filter((_, index) => index !== checkboxIndex),
      }
    }
  }

  const checkboxIndex = childArray.findIndex(isCheckboxInput)
  if (checkboxIndex >= 0) {
    const content = childArray.filter((child, index) => {
      if (index === checkboxIndex) return false
      if (index === checkboxIndex + 1 && typeof child === 'string' && child.trim() === '') {
        return false
      }
      return true
    })
    return { checkbox: childArray[checkboxIndex], content }
  }

  return { checkbox: null, content: childArray }
}

function TaskListItem({
  children,
  className,
}: {
  children?: ReactNode
  className?: string
}) {
  const { checkbox, content } = splitTaskItemChildren(children)

  return (
    <li className={cn('task-list-item', className)}>
      {checkbox ? <span className="task-list-item-checkbox">{checkbox}</span> : null}
      <span className="task-list-item-content">{content}</span>
    </li>
  )
}

interface MarkdownPreviewProps {
  content: string
  className?: string
  onInternalLinkClick?: (href: string, event?: MouseEvent) => void
  /** Passed to Mermaid "Fix in chat" so the agent can patchDocument on the open doc. */
  documentId?: string | null
  documentTitle?: string | null
  documentPath?: string | null
  documentType?: string | null
  /** DB frontmatter. Locker MP3s keep attachment_id here and leave the body empty. */
  documentFrontmatter?: Record<string, unknown> | null
  /**
   * When true, skips the centred 760px reading column and renders inline/full-width.
   * Use for compact contexts like product board cards, panel previews, etc.
   */
  compact?: boolean
  /** Max width of the inner reading column (e.g. `760px`). Use `none` for full panel width with padding. */
  maxWidth?: string
  /**
   * Inner reading column padding. `default` matches TipTap (2rem). `panel` is tighter for narrow
   * side previews so outer wrappers do not need extra padding (avoid double gutters).
   */
  readingColumnPadding?: "default" | "panel"
  /**
   * Document viewer preview: margin bookmark + add-to-chat (when agent panel is open).
   * Off for compact/side previews.
   */
  enableReadingMargin?: boolean
  /**
   * When set, GFM task checkboxes write back into the markdown source
   * (Preview mode). Omit for read-only / compact previews.
   */
  onToggleTask?: (index: number, checked: boolean) => void
  /**
   * When set, the basecamp-draft card status dropdown writes `status:` back
   * into the markdown source (Preview mode). Omit for read-only previews.
   */
  onBasecampDraftStatus?: (fenceSource: string, status: string) => void
}

export function MarkdownPreview({
  content,
  className = "",
  onInternalLinkClick,
  documentId = null,
  documentTitle = null,
  documentPath = null,
  documentType = null,
  documentFrontmatter = null,
  compact = false,
  maxWidth = "760px",
  readingColumnPadding = "default",
  enableReadingMargin = false,
  onToggleTask,
  onBasecampDraftStatus,
}: MarkdownPreviewProps) {
  const parsedFm = parseMarkdownWithFrontmatter(content)
  const isSessionImage = String(parsedFm.frontmatter?.kind || '') === 'session_image'
  const body = isSessionImage
    ? ensureSessionImageMarkdown(content, parsedFm.frontmatter)
    : stripFrontmatter(content)
  const isAudioLocker =
    String(documentType || '').toLowerCase() === 'audio' ||
    /\.mp3$/i.test(documentPath || '')
  const lockerMeta = {
    ...(parsedFm.frontmatter || {}),
    ...(documentFrontmatter || {}),
  }
  const spokenBody = stripLeadingAudioFence(body)
  const inlineAudioFence = isAudioLocker ? null : leadingAudioFence(body)
  const lockerFence = isAudioLocker
    ? parseAudioFenceYaml(
        [
          lockerMeta.attachment_id != null
            ? `attachment_id: ${String(lockerMeta.attachment_id)}`
            : '',
          lockerMeta.audio_attachment_id != null
            ? `attachment_id: ${String(lockerMeta.audio_attachment_id)}`
            : '',
          lockerMeta.voice_id != null ? `voice_id: ${String(lockerMeta.voice_id)}` : '',
          lockerMeta.source_hash != null
            ? `source_hash: ${String(lockerMeta.source_hash)}`
            : '',
        ]
          .filter(Boolean)
          .join('\n'),
      )
    : null
  const agentPanelVisible = useAgentPanelVisible()
  const { bookmark } = useSoftBookmark(enableReadingMargin ? documentId : null)
  const { enabled: bookmarksEnabled } = useSoftBookmarksEnabled()
  const readingMarginActive = enableReadingMargin && !compact && Boolean(documentId)
  const rootRef = useRef<HTMLDivElement>(null)
  /** Hide gutter icons when the scroll panel has no side margin for them. */
  const [gutterSpaceOk, setGutterSpaceOk] = useState(true)
  const showBookmark = readingMarginActive && gutterSpaceOk && bookmarksEnabled
  const showAddToChat = readingMarginActive && gutterSpaceOk && agentPanelVisible

  useEffect(() => {
    if (!readingMarginActive) {
      setGutterSpaceOk(false)
      return
    }
    const root = rootRef.current
    if (!root) return
    const container =
      (root.closest('[data-scroll-container]') as HTMLElement | null) ??
      (root.parentElement as HTMLElement | null)
    if (!container) return

    const GUTTER_MIN_PX = 44 // w-11 icon column
    const measure = () => {
      const prose = root.querySelector('.document-reading-prose') as HTMLElement | null
      const cw = container.clientWidth
      const proseW = prose?.offsetWidth ?? Math.min(760, cw)
      const side = Math.max(0, (cw - proseW) / 2)
      setGutterSpaceOk(side >= GUTTER_MIN_PX)
    }
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(container)
    const prose = root.querySelector('.document-reading-prose')
    if (prose) ro.observe(prose)
    return () => ro.disconnect()
  }, [readingMarginActive, maxWidth])

  /** Full-size overlay; src is the raw markdown URL (AuthenticatedImage fetches a blob). */
  const [imageLightbox, setImageLightbox] = useState<{ src: string; altCaption: string } | null>(null)

  /** Callers often pass an inline handler (new function each render); keep latest in ref so `components` stays stable. */
  const onInternalLinkClickRef = useRef(onInternalLinkClick)
  onInternalLinkClickRef.current = onInternalLinkClick
  const onToggleTaskRef = useRef(onToggleTask)
  onToggleTaskRef.current = onToggleTask
  const taskToggleEnabled = Boolean(onToggleTask)
  const onBasecampDraftStatusRef = useRef(onBasecampDraftStatus)
  onBasecampDraftStatusRef.current = onBasecampDraftStatus
  const draftStatusEnabled = Boolean(onBasecampDraftStatus)

  const openImagePreview = useCallback((rawSrc: string, altCaption: string) => {
    if (!rawSrc) return
    setImageLightbox({ src: rawSrc, altCaption })
  }, [])

  const wrapBlock = useCallback(
    (
      tag: 'p' | 'h1' | 'h2' | 'h3' | 'h4' | 'blockquote' | 'pre' | 'li' | 'div',
      children: React.ReactNode,
      opts?: { id?: string; className?: string }
    ) => {
      // Bookmarks only on H1-H4. Nested blockquote+p both used to render left
      // gutters, which looked like a double bookmark on quotes.
      const bookmarkOnBlock =
        showBookmark && (tag === 'h1' || tag === 'h2' || tag === 'h3' || tag === 'h4')
      const useGutter = readingMarginActive && (bookmarkOnBlock || showAddToChat)
      if (!useGutter) {
        const Tag = tag
        return (
          <Tag id={opts?.id} className={opts?.className}>
            {children}
          </Tag>
        )
      }
      // Bookmark highlight comes from PreviewBookmarkedHeadingContext so ReactMarkdown
      // `components` stay stable (avoids full remount + scroll jump on bookmark click).
      return (
        <PreviewReadingGutter
          tag={tag}
          id={opts?.id}
          className={opts?.className}
          documentId={documentId}
          documentTitle={documentTitle}
          documentPath={documentPath}
          markdownSource={body}
          showAddToChat={showAddToChat}
          showBookmark={bookmarkOnBlock}
        >
          {children}
        </PreviewReadingGutter>
      )
    },
    [
      readingMarginActive,
      documentId,
      documentTitle,
      documentPath,
      body,
      showAddToChat,
      showBookmark,
    ]
  )

  const taskIndexRef = useRef(0)
  taskIndexRef.current = 0

  const components = useMemo(
    () => ({
      h1: ({ children }: { children?: React.ReactNode }) =>
        wrapBlock('h1', children, { id: slugifyHeading(childrenToText(children)) }),
      h2: ({ children }: { children?: React.ReactNode }) =>
        wrapBlock('h2', children, { id: slugifyHeading(childrenToText(children)) }),
      h3: ({ children }: { children?: React.ReactNode }) =>
        wrapBlock('h3', children, { id: slugifyHeading(childrenToText(children)) }),
      h4: ({ children }: { children?: React.ReactNode }) =>
        wrapBlock('h4', children, { id: slugifyHeading(childrenToText(children)) }),
      p: ({ children }: { children?: React.ReactNode }) => wrapBlock('p', children),
      ul: ({
        children,
        className,
      }: {
        children?: React.ReactNode
        className?: string
      }) => <ul className={className}>{children}</ul>,
      ol: ({ children }: { children?: React.ReactNode }) => <ol>{children}</ol>,
      li: ({
        children,
        className,
        id,
      }: {
        children?: React.ReactNode
        className?: string
        id?: string
      }) => {
        if (className?.includes('task-list-item')) {
          return <TaskListItem className={className}>{children}</TaskListItem>
        }
        // No per-line gutters on list items - too noisy; use paragraph/heading blocks.
        // Keep `id`: footnote definitions are `<li id="user-content-...fn-N">` (scroll target).
        return (
          <li id={id} className={className}>{children}</li>
        )
      },
      input: ({
        type,
        checked,
        disabled,
        ...props
      }: InputHTMLAttributes<HTMLInputElement>) => {
        if (type === 'checkbox') {
          const index = taskIndexRef.current++
          const interactive = taskToggleEnabled
          return (
            <input
              {...props}
              type="checkbox"
              checked={Boolean(checked)}
              disabled={!interactive}
              readOnly={!interactive}
              data-testid={testIds.markdownPreview.taskCheckbox}
              onClick={
                interactive
                  ? (e) => {
                      e.stopPropagation()
                    }
                  : undefined
              }
              onDoubleClick={
                interactive
                  ? (e) => {
                      e.stopPropagation()
                    }
                  : undefined
              }
              onChange={
                interactive
                  ? (e) => {
                      e.stopPropagation()
                      onToggleTaskRef.current?.(index, e.target.checked)
                    }
                  : undefined
              }
            />
          )
        }
        return <input type={type} checked={checked} disabled={disabled} {...props} />
      },
      strong: ({ children }: { children?: React.ReactNode }) => <strong>{children}</strong>,
      em: ({ children }: { children?: React.ReactNode }) => <em>{children}</em>,
      code: ({
        children,
        className: codeClassName,
      }: {
        children?: React.ReactNode
        className?: string
      }) => {
        const isInline = !codeClassName
        if (isInline) {
          return <code>{children}</code>
        }
        return <code className={codeClassName}>{children}</code>
      },
      pre: ({ children }: { children?: React.ReactNode }) => {
        const mermaidSource = getMermaidSourceFromPreChildren(children)
        if (mermaidSource !== null) {
          return (
            <MermaidDiagram
              source={mermaidSource}
              documentId={documentId}
              documentTitle={documentTitle}
              documentPath={documentPath}
            />
          )
        }
        const statusBoardSource = getStatusBoardSourceFromPreChildren(children)
        if (statusBoardSource !== null) {
          return <StatusBoardFenceView source={statusBoardSource} />
        }
        if (getReviewChangelogSourceFromPreChildren(children) !== null) {
          return null
        }
        const basecampDraft = getBasecampDraftSourceFromPreChildren(children)
        if (basecampDraft) {
          return (
            <BasecampDraftFenceView
              source={basecampDraft.source}
              language={basecampDraft.language}
              onStatusChange={
                draftStatusEnabled
                  ? (fenceSource, status) =>
                      onBasecampDraftStatusRef.current?.(fenceSource, status)
                  : undefined
              }
            />
          )
        }
        const htmlSource = getHtmlSourceFromPreChildren(children)
        if (htmlSource !== null) {
          return <HtmlBlockPreview html={htmlSource} />
        }
        const audioFence = getAudioSourceFromPreChildren(children)
        if (audioFence) {
          return (
            <AudioFencePlayerLive
              fence={audioFence}
              documentId={documentId}
              lessonBody={body}
              onGenerated={() => {
                if (documentId) {
                  window.dispatchEvent(
                    new CustomEvent('document-updated', { detail: { documentId } }),
                  )
                }
              }}
            />
          )
        }
        return wrapBlock('pre', children)
      },
      blockquote: ({ children }: { children?: React.ReactNode }) =>
        wrapBlock('blockquote', children),
      hr: () => <hr />,
      a: ({ children, href, id }: { children?: React.ReactNode; href?: string; id?: string }) => {
        // Same-page fragments (footnote refs/backrefs, heading links) are not document links.
        // Keep `id`: the footnote marker `<a id="user-content-...fnref-N">` is the backref target.
        if (href && isInPageFragmentHref(href)) {
          return (
            <a
              id={id}
              href={href}
              onClick={(e) => {
                e.preventDefault()
                scrollToInPageAnchor(rootRef.current, href)
              }}
            >
              {children}
            </a>
          )
        }
        const localDevHref = href ? absoluteLocalDevHref(href) : null
        if (localDevHref) {
          return (
            <a href={localDevHref} target="_blank" rel="noopener noreferrer">
              {children}
            </a>
          )
        }
        if (href && isMaaterNonDocumentAppHref(href)) {
          const appHref = hrefForInAppNavigation(href)
          if (appHref) {
            return <InAppRouteLink to={appHref}>{children}</InAppRouteLink>
          }
        }
        const inAppHref = href ? hrefForInAppDocumentNavigation(href) : null
        const handler = onInternalLinkClickRef.current
        if (inAppHref && handler) {
          return (
            <a
              href={inAppHref}
              onClick={(e) => {
                e.preventDefault()
                handler(inAppHref, e.nativeEvent as MouseEvent)
              }}
            >
              {children}
            </a>
          )
        }
        if (inAppHref) {
          return <a href={inAppHref}>{children}</a>
        }
        return (
          <a href={href} target="_blank" rel="noopener noreferrer">
            {children}
          </a>
        )
      },
      img: (props: { src?: string; alt?: string; width?: string | number }) => (
        <MarkdownImage {...props} onOpenPreview={openImagePreview} />
      ),
      table: ({ children, ...props }: TableHTMLAttributes<HTMLTableElement>) =>
        wrapBlock(
          'div',
          <table {...props}>{children}</table>,
          { className: 'table-scroll-wrapper' }
        ),
    }),
    [
      openImagePreview,
      documentId,
      documentTitle,
      documentPath,
      wrapBlock,
      readingMarginActive,
      body,
      showAddToChat,
      taskToggleEnabled,
      draftStatusEnabled,
    ],
  )

  return (
    <div ref={rootRef} className={`markdown-preview text-foreground ${className}`}>
      <ImageLightboxDialog
        src={imageLightbox?.src ?? null}
        altCaption={imageLightbox?.altCaption}
        onClose={() => setImageLightbox(null)}
      />
      {/* Reading column: centred with padding matching TipTap's 2rem inset. Skipped in compact mode.
          Bookmark / add-to-chat sit outside the text (absolute) - never add pl-12 that shifts preview vs edit. */}
      <div
        className={cn(
          'document-reading-prose overflow-visible',
          readingColumnPadding === 'panel' && 'document-reading-prose--panel',
          !compact && 'mx-auto w-full box-border',
          !compact &&
            (readingColumnPadding === 'panel'
              ? 'px-4 py-4 sm:px-5 sm:py-5'
              : 'px-8 py-8'),
        )}
        style={
          compact || maxWidth === "none"
            ? undefined
            : { maxWidth }
        }
      >
        <PreviewBookmarkedHeadingContext.Provider value={bookmark?.headingId ?? null}>
          {lockerFence ? (
            <AudioFencePlayerLive
              fence={lockerFence}
              documentId={documentId}
              lessonBody={spokenBody}
            />
          ) : inlineAudioFence ? (
            <AudioFencePlayerLive
              fence={inlineAudioFence}
              documentId={documentId}
              lessonBody={spokenBody}
            />
          ) : null}
          <ReactMarkdown
            remarkPlugins={MARKDOWN_REMARK_PLUGINS}
            rehypePlugins={MARKDOWN_REHYPE_PLUGINS}
            urlTransform={chatMarkdownUrlTransform}
            components={components}
          >
            {inlineAudioFence ? spokenBody : body}
          </ReactMarkdown>
        </PreviewBookmarkedHeadingContext.Provider>
      </div>
    </div>
  )
}
